import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, CircularProgress, Button, Pagination } from '@mui/material';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import ProductCard from './ProductCard';
import { InventoryItem } from '../../utils/types';
import { getStoreProducts } from '../../services/storeService';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';


interface ProductListProps {
  categoryId?: string;
  searchQuery?: string;
}

const ProductList: React.FC<ProductListProps> = ({ categoryId, searchQuery }) => {
  const { settings } = useStoreSettings();
  const [products, setProducts] = useState<InventoryItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;

  const loadProducts = async (newCategory: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      // إذا كان هناك تغيير في الفئة، نعيد تعيين الصفحة والمنتجات
      if (newCategory) {
        setProducts([]);
        setLastVisible(undefined);
        setPage(1);
      }

      console.log('Loading products with categoryId:', categoryId);

      const result = await getStoreProducts(
        categoryId,
        newCategory ? undefined : lastVisible,
        itemsPerPage
      );

      if (!result || !result.products) {
        console.error('Invalid result from getStoreProducts:', result);
        setError('حدث خطأ أثناء تحميل المنتجات. يرجى المحاولة مرة أخرى.');
        setLoading(false);
        return;
      }

      if (newCategory) {
        setProducts(result.products);
      } else {
        setProducts(prev => [...prev, ...result.products]);
      }

      setLastVisible(result.lastVisible || undefined);
      setHasMore(result.products.length === itemsPerPage);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('حدث خطأ أثناء تحميل المنتجات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('ProductList: categoryId changed to', categoryId);
    loadProducts(true);
  }, [categoryId]);

  // تصفية المنتجات حسب البحث
  const filteredProducts = searchQuery
    ? products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : products;

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadProducts();
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" onClick={() => loadProducts(true)} sx={{ mt: 2 }}>
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  if (filteredProducts.length === 0) {
    console.log('ProductList: No products found for categoryId', categoryId);
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">
          لا توجد منتجات متاحة{searchQuery ? ' تطابق بحثك' : ' في هذه الفئة'}
        </Typography>
        <Button variant="contained" onClick={() => loadProducts(true)} sx={{ mt: 2 }}>
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  // حساب المنتجات المعروضة في الصفحة الحالية
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageProducts = filteredProducts.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {currentPageProducts.map((product) => {
          const desktopCols = settings?.display?.productCardColumns || 4;
          const mobileCols = settings?.display?.mobileProductColumns || 2;
          const xsVal = mobileCols === 1 ? 12 : 6;
          const smVal = desktopCols > 2 ? 6 : 12;
          const mdVal = desktopCols === 4 ? 4 : (desktopCols === 3 ? 4 : 6);
          const lgVal = 12 / desktopCols;
          
          return (
            <Grid item xs={xsVal} sm={smVal} md={mdVal} lg={lgVal} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          );
        })}

      </Grid>

      {filteredProducts.length > itemsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {!searchQuery && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'تحميل المزيد'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProductList;
